import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, CreditCard, XCircle, CheckCircle2, AlertCircle, Lock, ArrowRight, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { generateOrderNumber } from '../utils/orderNumber';
import { openTranzilaPayment } from '../utils/tranzila';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
const PAYMENT_WEBHOOK_URL = 'https://hook.eu2.make.com/ijcmimuvqdy2jlm4dw9srvba9g2ndkub';
const CANCEL_WEBHOOK_URL = 'https://hook.eu2.make.com/u9r2klw104wru5uhotc67rlx1tr3b1gi';
const UPGRADE_PRICE = 240;
export function AdditionalPaymentPage() {
  const [activeTab, setActiveTab] = useState<'payment' | 'cancel' | null>(null);
  // Payment State
  const [payForm, setPayForm] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [showIframe, setShowIframe] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Cancel State
  const [cancelForm, setCancelForm] = useState({
    name: '',
    email: '',
    phone: '',
    reason: '',
    otherReason: ''
  });
  const [isSubmittingCancel, setIsSubmittingCancel] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancelError, setCancelError] = useState(false);
  // ============================================
  // TEST MODE
  // ============================================
  const [isTestMode, setIsTestMode] = useState(false);
  useEffect(() => {
    document.title = 'עדכון תאריכים - Water Tribe Fest 2026';
    const urlParams = new URLSearchParams(window.location.search);
    const testModeParam = urlParams.get('test_mode');
    if (testModeParam === 'watertribe2026') {
      setIsTestMode(true);
      localStorage.setItem('test_mode_active', 'true');
      console.log('🧪 TEST MODE ACTIVATED - Price set to ₪1');
    } else {
      const storedTestMode = localStorage.getItem('test_mode_active');
      if (storedTestMode === 'true') {
        setIsTestMode(true);
        console.log('🧪 TEST MODE ACTIVE (from localStorage)');
      }
    }
  }, []);
  // Derived price based on test mode
  const finalPrice = isTestMode ? 1 : UPGRADE_PRICE;
  const finalPriceDisplay = isTestMode ? '1' : UPGRADE_PRICE.toString();
  // --- Handlers ---
  const handlePaymentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const orderNumber = generateOrderNumber();
    const submissionData = {
      name: payForm.name,
      email: payForm.email,
      phone: payForm.phone,
      orderNumber: orderNumber,
      type: 'תוספת תשלום שדרוג',
      price: finalPrice.toString(),
      source: 'דף שדרוג תאריכים',
      webhookUrl: PAYMENT_WEBHOOK_URL
    };
    localStorage.setItem('pendingOrder', JSON.stringify({
      orderNumber,
      name: payForm.name,
      email: payForm.email,
      timestamp: Date.now()
    }));
    localStorage.setItem('pendingRegistrationData', JSON.stringify(submissionData));
    setShowIframe(true);
    setTimeout(() => {
      openTranzilaPayment({
        sum: finalPrice,
        contact: payForm.name,
        email: payForm.email,
        phone: payForm.phone,
        pdesc: 'תוספת תשלום - שדרוג תאריכים',
        success_url: `${window.location.origin}/tranzila-success`,
        fail_url: window.location.href,
        u1: orderNumber,
        u2: 'תוספת תשלום',
        u3: finalPrice.toString()
      });
      setTimeout(() => {
        iframeRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }, 100);
  };
  const handleCancelSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingCancel(true);
    setCancelError(false);
    try {
      const payload = {
        type: 'בקשת ביטול',
        name: cancelForm.name,
        email: cancelForm.email,
        phone: cancelForm.phone,
        cancelReason: cancelForm.reason,
        cancelDetails: cancelForm.reason === 'אחר' ? cancelForm.otherReason : '',
        timestamp: new Date().toISOString()
      };
      const res = await fetch(CANCEL_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to submit cancellation');
      setCancelSuccess(true);
    } catch (err) {
      setCancelError(true);
    } finally {
      setIsSubmittingCancel(false);
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex flex-col" dir="rtl" data-id="element-875" data-mp="1fbndfmsu7p6t">
      <Navigation data-id="element-876" data-mp="1tbepvj3pgxip" />

      {/* TEST MODE INDICATOR */}
      {isTestMode && <div className="fixed top-4 left-4 z-50 bg-yellow-500 text-black px-4 py-2 rounded-lg shadow-lg font-bold text-sm flex items-center gap-2" data-id="element-877" data-mp="1o9z2vlhywgxo">
          <Zap className="w-4 h-4" data-id="element-878" data-mp="34r3c2894xsig" />
          TEST MODE - ₪1
        </div>}

      <main className="flex-grow container mx-auto px-4 py-12 md:py-20 flex flex-col items-center justify-center" data-id="element-879" data-mp="2zpnu4p29824a">
        {/* Header Section */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="max-w-3xl w-full text-center mb-12" data-id="element-880" data-mp="3c8jgcqmoff1m">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full font-bold mb-6" data-id="element-881" data-mp="2ybczj1lijqf1">
            <Calendar className="w-5 h-5" data-id="element-882" data-mp="3qxxl1lfb58ao" />
            <span data-id="element-883" data-mp="3qy7ejizk9xl4">עדכון חשוב</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-black text-[#0A5F8F] mb-6 leading-tight" data-id="element-884" data-mp="3vwip5vxokc4e">
            הפסטיבל שודרג לתאריכים
            <br data-id="element-885" data-mp="3h6uq71cz1pju" />
            <span className="text-cyan-500" data-id="element-886" data-mp="3h7crwdiekvz5">19-21 בנובמבר 2026!</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto" data-id="element-887" data-mp="3m5o3asocjejh">
            בעקבות המעבר לשיא העונה בכנרת — עם מזג אוויר מושלם וחוויה משודרגת —
            ישנה תוספת תשלום של{' '}
            <strong className="text-gray-900" data-id="element-888" data-mp="ijgpbd6gsqki">₪{finalPrice}</strong> למשתתף.
          </p>
        </motion.div>

        {/* Main Content Area */}
        <div className="max-w-4xl w-full" data-id="element-889" data-mp="nerpp8cog5ya">
          {/* Tabs Selection */}
          {!activeTab && <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-890" data-mp="ifoltr8fo5rw">
              {/* Payment Option */}
              <button onClick={() => setActiveTab('payment')} className="bg-white rounded-3xl p-8 shadow-xl border-2 border-transparent hover:border-cyan-400 transition-all group text-right flex flex-col h-full" data-id="element-891" data-mp="9g1u5x5iaec1">
                <div className="bg-cyan-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-892" data-mp="3vvcra42zbwrd">
                  <CheckCircle2 className="w-8 h-8 text-cyan-600" data-id="element-893" data-mp="3mtn3d88b3fyc" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-894" data-mp="3hutobbddfgw2">
                  אני ממשיך! 🎉
                </h2>
                <p className="text-gray-600 mb-8 flex-grow" data-id="element-895" data-mp="1xdas657eqg91">
                  מעולה! לחצו כאן להשלמת תוספת התשלום (₪{finalPrice}) והבטחת
                  מקומכם בתאריכים החדשים.
                </p>
                <div className="flex items-center text-cyan-600 font-bold gap-2 group-hover:gap-3 transition-all" data-id="element-896" data-mp="26cxviwjp5w4q">
                  <span data-id="element-897" data-mp="2bbhfireqcxug">מעבר לתשלום</span>
                  <ArrowRight className="w-5 h-5 rotate-180" data-id="element-898" data-mp="2bag2ihe5m1a3" />
                </div>
              </button>

              {/* Cancel Option */}
              <button onClick={() => setActiveTab('cancel')} className="bg-white rounded-3xl p-8 shadow-xl border-2 border-transparent hover:border-rose-400 transition-all group text-right flex flex-col h-full" data-id="element-899" data-mp="1in3hibnn61iw">
                <div className="bg-rose-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-900" data-mp="1sepzqryed0v5">
                  <XCircle className="w-8 h-8 text-rose-600" data-id="element-901" data-mp="uwg5pfalurr2" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-902" data-mp="zvao4g4nuean">
                  אני מעוניין לבטל 😔
                </h2>
                <p className="text-gray-600 mb-8 flex-grow" data-id="element-903" data-mp="3kgm4oqmrouqe">
                  מבינים לגמרי. לחצו כאן למילוי טופס קצר לביטול ההשתתפות וקבלת
                  החזר כספי.
                </p>
                <div className="flex items-center text-rose-600 font-bold gap-2 group-hover:gap-3 transition-all" data-id="element-904" data-mp="3uejsjd0pspih">
                  <span data-id="element-905" data-mp="c8nhuunizu1i">לבקשת ביטול</span>
                  <ArrowRight className="w-5 h-5 rotate-180" data-id="element-906" data-mp="c7ch73pdn2em" />
                </div>
              </button>
            </motion.div>}

          {/* Payment Form */}
          <AnimatePresence mode="wait" data-id="element-907" data-mp="lz8guj7430ky">
            {activeTab === 'payment' && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100" data-id="element-908" data-mp="m0jl0k4gyacl">
                <div className="bg-gradient-to-r from-[#0A5F8F] to-cyan-600 p-6 md:p-8 text-white flex items-center justify-between" data-id="element-909" data-mp="19m402onnn9yy">
                  <div data-id="element-910" data-mp="1elgmj8am6f10">
                    <h2 className="text-2xl font-bold mb-2" data-id="element-911" data-mp="1ekopru5a6b95">השלמת תשלום</h2>
                    <p className="text-cyan-100" data-id="element-912" data-mp="10m78ae5yxuzo">
                      תוספת שדרוג תאריכים: ₪{finalPrice}
                    </p>
                  </div>
                  <button onClick={() => {
                setActiveTab(null);
                setShowIframe(false);
              }} className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors" data-id="element-913" data-mp="14ovhimylh3x6">
                    <ArrowRight className="w-6 h-6" data-id="element-914" data-mp="gxg684oubc9s" />
                  </button>
                </div>

                <div className="p-6 md:p-8" data-id="element-915" data-mp="lw9osbizhtgt">
                  {!showIframe ? <form onSubmit={handlePaymentSubmit} className="space-y-6 max-w-md mx-auto" data-id="element-916" data-mp="lwsw6yb0czgl">
                      <div data-id="element-917" data-mp="7xjqiskfhnqv">
                        <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-918" data-mp="cvv0d3aq3y93">
                          שם מלא
                        </label>
                        <input type="text" required value={payForm.name} onChange={e => setPayForm({
                    ...payForm,
                    name: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all outline-none" placeholder="ישראל ישראלי" data-id="element-919" data-mp="1pnn8mof916o4" />
                      </div>
                      <div data-id="element-920" data-mp="29dp5i87uhmsz">
                        <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-921" data-mp="29eig5k9xcwge">
                          אימייל (שאיתו נרשמתם)
                        </label>
                        <input type="email" required value={payForm.email} onChange={e => setPayForm({
                    ...payForm,
                    email: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all outline-none text-right" dir="ltr" placeholder="israel@example.com" data-id="element-922" data-mp="1zlt7ex5rdwor" />
                      </div>
                      <div data-id="element-923" data-mp="36epctmmcln5o">
                        <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-924" data-mp="32fgjujz4ttb6">
                          טלפון
                        </label>
                        <input type="tel" required value={payForm.phone} onChange={e => setPayForm({
                    ...payForm,
                    phone: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all outline-none text-right" dir="ltr" placeholder="050-0000000" data-id="element-925" data-mp="3g63jqfgmpp8u" />
                      </div>

                      <Button type="submit" className="w-full py-4 text-lg font-bold bg-gradient-to-r from-[#0A5F8F] to-cyan-600 hover:from-[#084b72] hover:to-cyan-700 rounded-xl shadow-lg flex items-center justify-center gap-2" data-id="element-926" data-mp="2xe0zyv8oxsud">
                        <CreditCard className="w-5 h-5" data-id="element-927" data-mp="7r1wv5wc41ff" />
                        מעבר לתשלום מאובטח (₪{finalPrice})
                      </Button>
                    </form> : <div className="w-full" data-id="element-928" data-mp="fr7zhzux06xt">
                      <div className="bg-gray-50 p-4 border border-gray-200 rounded-t-xl flex items-center justify-between mb-[-1px] relative z-10" data-id="element-929" data-mp="jstaffnw2b5d">
                        <div className="flex items-center gap-2 text-gray-600" data-id="element-930" data-mp="osz38z5wa3p1">
                          <Lock className="w-4 h-4" data-id="element-931" data-mp="tpxwlfhqi6m6" />
                          <span className="text-sm font-medium" data-id="element-932" data-mp="3s8wi3lf03ztp">
                            תשלום מאובטח באמצעות טרנזילה
                          </span>
                        </div>
                        <div className="text-sm font-bold text-[#0A5F8F]" data-id="element-933" data-mp="108sgt9q9qgc">
                          ₪{finalPrice}
                        </div>
                      </div>
                      <iframe ref={iframeRef} name="tranzila" width="100%" height="600" frameBorder="0" allow="payment" className="w-full border border-gray-200 rounded-b-xl" data-id="element-934" data-mp="atzcnlytneq8"></iframe>
                    </div>}
                </div>
              </motion.div>}

            {/* Cancel Form */}
            {activeTab === 'cancel' && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 max-w-2xl mx-auto w-full" data-id="element-935" data-mp="36vw6z5z09ljh">
                <div className="bg-gradient-to-r from-rose-500 to-red-600 p-6 md:p-8 text-white flex items-center justify-between" data-id="element-936" data-mp="2nyuocicj3mx2">
                  <div data-id="element-937" data-mp="2j7cwcjshgwo8">
                    <h2 className="text-2xl font-bold mb-2" data-id="element-938" data-mp="2j736cw7fupij">בקשת ביטול</h2>
                    <p className="text-rose-100" data-id="element-939" data-mp="2eacp45jwdnvj">
                      נשמח להבין למה כדי להשתפר לפעם הבאה
                    </p>
                  </div>
                  {!cancelSuccess && <button onClick={() => setActiveTab(null)} className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors" data-id="element-940" data-mp="24bn8sh7ngbhe">
                      <ArrowRight className="w-6 h-6" data-id="element-941" data-mp="20a1zf5ubqfby" />
                    </button>}
                </div>

                <div className="p-6 md:p-8" data-id="element-942" data-mp="2p0y6og3k49cn">
                  {cancelSuccess ? <div className="text-center py-8" data-id="element-943" data-mp="2tzix5nt0benw">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-944" data-mp="2u13shfn5b1mt">
                        <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-945" data-mp="2k97tm2d8j7hj" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-946" data-mp="2695eup084u6i">
                        בקשתך התקבלה
                      </h3>
                      <p className="text-gray-600 text-lg mb-8" data-id="element-947" data-mp="2backoiit3jaj">
                        ניצור קשר בהקדם להשלמת תהליך הביטול וההחזר הכספי.
                      </p>
                      <Button variant="secondary" onClick={() => window.location.href = '/'} data-id="element-948" data-mp="219ttt3str2j4">
                        חזרה לדף הבית
                      </Button>
                    </div> : <form onSubmit={handleCancelSubmit} className="space-y-6" data-id="element-949" data-mp="3vomnuye27l8y">
                      {cancelError && <div className="bg-red-50 text-red-700 p-4 rounded-xl flex items-start gap-3 border border-red-200" data-id="element-950" data-mp="3vma5wl8axavw">
                          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-951" data-mp="1g07ns4cvnbk1" />
                          <p data-id="element-952" data-mp="1tr2o2s9b8kc0">
                            אירעה שגיאה בשליחת הבקשה. אנא נסו שוב או צרו קשר.
                          </p>
                        </div>}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-953" data-mp="1oz1gvkr01jsu">
                        <div data-id="element-954" data-mp="23nxmzc9hwmqt">
                          <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-955" data-mp="23rd4elqn67ve">
                            שם מלא
                          </label>
                          <input type="text" required value={cancelForm.name} onChange={e => setCancelForm({
                      ...cancelForm,
                      name: e.target.value
                    })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none" data-id="element-956" data-mp="2covs5xvo70az" />
                        </div>
                        <div data-id="element-957" data-mp="2dfvmb3dn8hu6">
                          <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-958" data-mp="2dgn85mhfv179">
                            טלפון
                          </label>
                          <input type="tel" required value={cancelForm.phone} onChange={e => setCancelForm({
                      ...cancelForm,
                      phone: e.target.value
                    })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none text-right" dir="ltr" data-id="element-959" data-mp="2ncqkfl608g7x" />
                        </div>
                      </div>

                      <div data-id="element-960" data-mp="3649jjgwwra4o">
                        <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-961" data-mp="3663wo91xtxy7">
                          אימייל (שאיתו נרשמתם)
                        </label>
                        <input type="email" required value={cancelForm.email} onChange={e => setCancelForm({
                    ...cancelForm,
                    email: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none text-right" dir="ltr" data-id="element-962" data-mp="3f60ouknjdey0" />
                      </div>

                      <div className="pt-4 border-t border-gray-100" data-id="element-963" data-mp="zg0s9vghbc8l">
                        <label className="block text-base font-bold text-gray-900 mb-4" data-id="element-964" data-mp="uhfu01ksf2ms">
                          סיבת הביטול:
                        </label>
                        <div className="space-y-3" data-id="element-965" data-mp="1n51wt7m6y38l">
                          {['התאריך החדש לא נוח לי', 'העלות הנוספת', 'אחר'].map(reason => <label key={reason} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-966" data-mp="1ie3bftmcieyh">
                                <input type="radio" name="cancelReason" value={reason} required checked={cancelForm.reason === reason} onChange={e => setCancelForm({
                        ...cancelForm,
                        reason: e.target.value
                      })} className="w-5 h-5 text-rose-600 focus:ring-rose-500" data-id="element-967" data-mp="1ddfmrellaa7f" />
                                <span className="text-gray-700 font-medium" data-id="element-968" data-mp="1j7cq48s4hwvp">
                                  {reason}
                                </span>
                              </label>)}
                        </div>
                      </div>

                      {cancelForm.reason === 'אחר' && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} data-id="element-969" data-mp="1x73ztxblwr3w">
                          <label className="block text-sm font-bold text-gray-700 mb-2" data-id="element-970" data-mp="1o6xn4dnqfv5s">
                            פרטו בבקשה:
                          </label>
                          <textarea required value={cancelForm.otherReason} onChange={e => setCancelForm({
                    ...cancelForm,
                    otherReason: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-rose-500 focus:border-rose-500 transition-all outline-none resize-none h-24" data-id="element-971" data-mp="1s9lwqnk9t63t" />
                        </motion.div>}

                      <Button type="submit" disabled={isSubmittingCancel} className="w-full py-4 text-lg font-bold bg-rose-600 hover:bg-rose-700 rounded-xl shadow-lg" data-id="element-972" data-mp="j6zhr7cyjof9">
                        {isSubmittingCancel ? 'שולח...' : 'שלח בקשת ביטול'}
                      </Button>
                    </form>}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </main>

      <Footer data-id="element-973" data-mp="8527w5o6jy1" />
    </div>;
}